import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Plus, RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { supabase } from '../../lib/supabase';
import { negotiationService } from '../../services/negotiationService';
import { ratingService } from '../../services/ratingService';
import NegotiationCard from './components/NegotiationCard';
import NegotiationFilters from './components/NegotiationFilters';
import NegotiationChat from './components/NegotiationChat';
import { useTranslation } from 'react-i18next';

function mapStatus(dbStatus) {
  const map = {
    initiated: 'otvoreno',
    open: 'otvoreno',
    pending: 'otvoreno',
    in_progress: 'u toku',
    active: 'u toku',
    agreed: 'zatvoreno',
    closed: 'zatvoreno',
    completed: 'zatvoreno',
    rejected: 'zatvoreno',
    cancelled: 'zatvoreno',
  };
  return map?.[dbStatus?.toLowerCase()] || dbStatus || 'otvoreno';
}

export default function MyNegotiationsMojaPregovaranja() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedNeg, setSelectedNeg] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [newNegBanner, setNewNegBanner] = useState(
    location?.state?.offerId ? location?.state?.offerName : null
  );
  const retryRef = useRef(0);
  const targetNegId = location?.state?.negotiationId;
  const [counterpartyRatings, setCounterpartyRatings] = useState({});

  useEffect(() => {
    initUser();
  }, []);

  // Auto-select negotiation when list loads (handles race condition with retries)
  useEffect(() => {
    if (!targetNegId || selectedNeg) return;
    const found = negotiations?.find(n => n?.id === targetNegId);
    if (found) {
      setSelectedNeg(found);
    } else if (!loading && retryRef?.current < 3) {
      // Negotiation not in list yet — retry fetch after short delay
      retryRef.current += 1;
      const timer = setTimeout(() => {
        fetchNegotiations();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [negotiations, loading, targetNegId, selectedNeg]);

  const initUser = async () => {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user) {
      setCurrentUserId(user?.id);
      fetchNegotiations();
    } else {
      setError('Morate biti prijavljeni da biste videli pregovaranja');
      setLoading(false);
    }
  };

  const fetchNegotiations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await negotiationService?.getMarketplaceNegotiations();
      setNegotiations(data);

      // Fetch counterparty ratings for all negotiations
      const uniqueIds = [...new Set(data?.map(n => n?.counterpartyId)?.filter(Boolean))];
      if (uniqueIds?.length > 0) {
        const ratingsMap = {};
        await Promise.all(
          uniqueIds?.map(async (uid) => {
            try {
              ratingsMap[uid] = await ratingService?.getUserRatingStats(uid);
            } catch {}
          })
        );
        setCounterpartyRatings(ratingsMap);
      }
    } catch (err) {
      setError(err?.message || 'Greška pri učitavanju pregovaranja');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusChange = (negId, newStatus) => {
    setNegotiations(prev =>
      prev?.map(n => n?.id === negId ? { ...n, status: newStatus } : n)
    );
    if (selectedNeg?.id === negId) {
      setSelectedNeg(prev => prev ? { ...prev, status: newStatus } : prev);
    }
  };

  const handleSelectNeg = (neg) => {
    setSelectedNeg(neg);
    setNewNegBanner(null);
  };

  // Filter
  const filtered = negotiations?.filter(n => {
    const displayStatus = mapStatus(n?.status);
    const matchSearch = !search ||
      n?.offerName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      n?.counterpartyName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !statusFilter || displayStatus === statusFilter?.toLowerCase();
    return matchSearch && matchStatus;
  });

  // Stats
  const stats = {
    total: negotiations?.length,
    otvoreno: negotiations?.filter(n => mapStatus(n?.status) === 'otvoreno')?.length,
    uToku: negotiations?.filter(n => mapStatus(n?.status) === 'u toku')?.length,
    zatvoreno: negotiations?.filter(n => mapStatus(n?.status) === 'zatvoreno')?.length,
  };

  // Format negotiations for NegotiationCard
  const formattedForCard = (neg) => ({
    ...neg,
    status: mapStatus(neg?.status),
    lastMessage: neg?.initialMessage,
    updatedAt: neg?.updatedAt || neg?.createdAt,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('negotiations.title')}</h1>
            <p className="text-gray-500 mt-1">{t('negotiations.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchNegotiations}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
            >
              <RefreshCw size={16} />
              {t('negotiations.refresh')}
            </button>
            <button
              onClick={() => navigate('/oglasna-tabla-board')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              {t('negotiations.newOffer')}
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
            <p className="text-xs text-gray-500 mt-1">{t('negotiations.total')}</p>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats?.otvoreno}</p>
            <p className="text-xs text-blue-500 mt-1">{t('negotiations.open')}</p>
          </div>
          <div className="bg-orange-50 rounded-xl border border-orange-200 p-4 text-center">
            <p className="text-2xl font-bold text-orange-700">{stats?.uToku}</p>
            <p className="text-xs text-orange-500 mt-1">{t('negotiations.inProgress')}</p>
          </div>
          <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{stats?.zatvoreno}</p>
            <p className="text-xs text-green-500 mt-1">{t('negotiations.closed')}</p>
          </div>
        </div>

        {/* New negotiation banner */}
        {newNegBanner && (
          <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-xl">
            <p className="text-teal-700 text-sm font-medium">
              {t('negotiations.newNegBanner', { name: newNegBanner })}
            </p>
          </div>
        )}

        {/* Loading banner when auto-selecting */}
        {targetNegId && !selectedNeg && loading && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <RefreshCw size={16} className="text-blue-500 animate-spin" />
            <p className="text-blue-700 text-sm">Učitavanje pregovaranja...</p>
          </div>
        )}

        {/* Main layout: list + chat */}
        <div className="flex gap-4" style={{ minHeight: '500px' }}>
          {/* Left: list */}
          <div className={`flex flex-col ${
            selectedNeg ? 'hidden md:flex md:w-80 lg:w-96 flex-shrink-0' : 'w-full'
          }`}>
            <NegotiationFilters
              search={search}
              onSearchChange={setSearch}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              activeTab=""
              onTabChange={() => {}}
            />

            {loading ? (
              <div className="space-y-3">
                {[...Array(3)]?.map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                    <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
                <p className="text-red-600 font-medium mb-2">{error}</p>
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg text-sm hover:bg-teal-800"
                >
                  Prijavi se
                </button>
              </div>
            ) : filtered?.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <MessageSquare size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  {search || statusFilter ? 'Nema pregovaranja koja odgovaraju filteru' : 'Nemate aktivnih pregovaranja'}
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                  {search || statusFilter
                    ? 'Pokušajte sa drugačijim filterima' :'Kliknite na oglas na Oglasnoj tabli i pokrenite pregovaranje'}
                </p>
                {!search && !statusFilter && (
                  <button
                    onClick={() => navigate('/oglasna-tabla-board')}
                    className="px-5 py-2.5 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors text-sm font-medium"
                  >
                    Idi na Oglasnu tablu
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 mb-2">
                  {filtered?.length} pregovaranje{filtered?.length !== 1 ? 'a' : ''}
                </p>
                {filtered?.map(neg => (
                  <div
                    key={neg?.id}
                    onClick={() => handleSelectNeg(neg)}
                    className={`cursor-pointer rounded-xl transition-all ${
                      selectedNeg?.id === neg?.id ? 'ring-2 ring-teal-500' : ''
                    }`}
                  >
                    <NegotiationCard
                      negotiation={formattedForCard(neg)}
                      onClick={() => handleSelectNeg(neg)}
                      counterpartyRating={counterpartyRatings?.[neg?.counterpartyId]}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: chat */}
          {selectedNeg ? (
            <div className="flex-1 flex flex-col" style={{ minHeight: '600px' }}>
              {/* Mobile back button */}
              <button
                onClick={() => setSelectedNeg(null)}
                className="md:hidden flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 text-sm"
              >
                <ArrowLeft size={16} /> Nazad na listu
              </button>
              <NegotiationChat
                negotiation={selectedNeg}
                currentUserId={currentUserId}
                onClose={() => setSelectedNeg(null)}
                onStatusChange={handleStatusChange}
                closeRequestedBy={selectedNeg?.closeRequestedBy}
              />
            </div>
          ) : (
            <div className="hidden md:flex flex-1 items-center justify-center bg-white rounded-xl border border-gray-200">
              <div className="text-center">
                <MessageSquare size={48} className="text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Izaberite pregovaranje iz liste</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
